/**
 * Ensure push subscription is using the correct VAPID key
 * Auto-re-subscribes if the key doesn't match
 */

import { safeGetItem } from '../storageSafe';
import authApiClient from '../../authApiClient'; // Fixed import path

// Utility function to convert base64 to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Utility function to compare two Uint8Arrays
function arraysEqual(a, b) {
  if (a.byteLength !== b.byteLength) return false;
  for (let i = 0; i < a.byteLength; i++) if (a[i] !== b[i]) return false;
  return true;
}

export async function ensurePushSubscription(registration) {
  try {
    console.log('🔍 Checking push subscription...');
    
    // Check notification permission first
    if (Notification.permission !== 'granted') {
      console.log('⚠️ Notification permission not granted, skipping push subscription');
      return null;
    }
    
    // Get the VAPID public key from environment and trim whitespace
    const vapidPublicKey = (process.env.REACT_APP_VAPID_PUBLIC_KEY || import.meta.env.VITE_VAPID_PUBLIC_KEY)?.trim();
    
    if (!vapidPublicKey) {
      console.error('❌ VAPID_PUBLIC_KEY not found in environment');
      return null;
    }
    
    // Convert to Uint8Array
    const want = urlBase64ToUint8Array(vapidPublicKey);
    
    // Get current subscription
    const sub = await registration.pushManager.getSubscription();
    
    // Check if subscription exists and has the correct key
    const equalKeys = s => {
      if (!s || !s.options || !s.options.applicationServerKey) return false;
      const currentKey = new Uint8Array(s.options.applicationServerKey);
      return arraysEqual(currentKey, want);
    };
    
    if (!equalKeys(sub)) {
      console.log('⚠️ Push subscription key mismatch - re-subscribing...');
      
      // Unsubscribe from old subscription
      if (sub) {
        await sub.unsubscribe();
        console.log('✅ Unsubscribed from old subscription');
      }
      
      // Subscribe with new key
      const fresh = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: want,
      });
      
      console.log('✅ Subscribed with new VAPID key');
      
      // Send new subscription to backend
      try {
        // Use authApiClient instead of manual token handling
        const response = await authApiClient.post('/push-subscription', fresh);
        
        if (response.status === 200 || response.status === 201) {
          console.log('✅ New subscription saved to backend');
        } else {
          console.error('❌ Failed to save subscription to backend:', response.status);
        }
      } catch (error) {
        console.error('❌ Error saving subscription to backend:', error);
        console.error('   Error details:', error.response?.data || error.message);
      }
      
      return fresh;
    } else {
      console.log('✅ Push subscription is using correct VAPID key');
      
      // IMPORTANT: Even if key matches, ensure subscription is saved to backend
      // This handles cases where subscription exists in browser but not in database
      if (sub) {
        console.log('🔍 Checking if subscription needs to be saved to backend...');
        try {
          // Check if subscription exists in backend
          const statusResponse = await authApiClient.get('/push-subscription');
          const hasBackendSubscription = statusResponse.data?.hasSubscription;
          
          if (!hasBackendSubscription) {
            console.log('⚠️ Subscription exists in browser but not in backend - saving now...');
            const saveResponse = await authApiClient.post('/push-subscription', sub);
            if (saveResponse.status === 200 || saveResponse.status === 201) {
              console.log('✅ Subscription saved to backend');
            } else {
              console.error('❌ Failed to save existing subscription:', saveResponse.status);
            }
          } else {
            console.log('✅ Subscription already exists in backend');
          }
        } catch (error) {
          console.error('❌ Error checking/saving subscription:', error);
          console.error('   Error details:', error.response?.data || error.message);
          // Try to save anyway - better to have duplicate saves than missing subscription
          try {
            console.log('🔄 Attempting to save subscription anyway...');
            const saveResponse = await authApiClient.post('/push-subscription', sub);
            console.log('✅ Subscription saved (fallback attempt)');
          } catch (saveError) {
            console.error('❌ Fallback save also failed:', saveError);
          }
        }
      }
      
      return sub;
    }
    
  } catch (error) {
    console.error('❌ Error ensuring push subscription:', error);
    return null;
  }
}

// Export utility functions for testing
export { urlBase64ToUint8Array, arraysEqual }; 